import React from 'react';
import {
    CollapsiblePanel,
    EditPanel,
    EditPanelButtons,
    EditPanelCell,
    EditPanelHeader,
    EditPanelRow,
    PanelBody,
    Tray,
} from '../../components/elements';
import { getOpenApiRouteItemUrlValidatingElements } from '../../components/validatingComponents/getOpenApiRouteItemUrlValidatingElements';
import { ValidatingEditPanelSaveButton, ValidatingFormContext, ValidatingNumberFilterDropdown } from '../../components/validatingElements';
import { useTranslate } from '../../mixins/translate/translate';
import { verificatieBerekeningSelectOptions } from './verificatieBerekeningSelectOptions';

const validatingElements = getOpenApiRouteItemUrlValidatingElements(
    '/api/surveys/{surveyId}/elementGroups/{elementGroupName}/inspectionStandardData/verificatieBerekening',
    'post',
);

function getConditionStyle(score?: number) {
    if (!score) {
        return undefined;
    } else if (score === 1) {
        return { color: 'white', backgroundColor: '#267b45', padding: '20px' };
    }
    return { color: 'white', backgroundColor: '#d73f2d', padding: '20px' };
}

export function SurveyEditVerificatieBerekeningAnalysisElementGroup(props: {
    elementGroupName: string;
    surveyId: string;
    objectId: string;
    onItemChanged: () => Promise<void>;
}) {
    const translate = useTranslate();

    return (
        <Tray>
            <EditPanel bottomMargin={true} noPadding={true}>
                <CollapsiblePanel
                    heading={{ dark: true, text: `Verificatie berekening: ${translate(`elementGroupName.${props.elementGroupName}`)}` }}
                >
                    <PanelBody isForm={true}>
                        <validatingElements.ItemUrlForm
                            onSubmitted={props.onItemChanged}
                            routeParams={{
                                surveyId: props.surveyId,
                                elementGroupName: props.elementGroupName,
                            }}
                        >
                            <EditPanelHeader text="Project"></EditPanelHeader>
                            <EditPanelRow>
                                <EditPanelCell rowCount={4}>
                                    {' '}
                                    <validatingElements.InputText name="projectNaam" topLabel="Project naam" />{' '}
                                </EditPanelCell>
                                <EditPanelCell rowCount={4}>
                                    {' '}
                                    <validatingElements.FilterDropdown
                                        name="status"
                                        selectOptions={verificatieBerekeningSelectOptions.status}
                                        topLabel="Status"
                                    />{' '}
                                </EditPanelCell>
                                <EditPanelCell rowCount={4}>
                                    {' '}
                                    <validatingElements.InputDate className="mt5" name="berekendOpDatum" topLabel="Datum" />{' '}
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelRow>
                                <EditPanelCell rowCount={12}>
                                    {' '}
                                    <validatingElements.InputText name="constructeur" topLabel="Constructeur" />{' '}
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelHeader text="Uitgangspunten"></EditPanelHeader>
                            <EditPanelRow>
                                <EditPanelCell rowCount={4}>
                                    {' '}
                                    <validatingElements.FilterDropdown
                                        name="toetsNiveau"
                                        selectOptions={verificatieBerekeningSelectOptions.toetsNiveau}
                                        topLabel="Toets niveau"
                                    />{' '}
                                </EditPanelCell>
                                <EditPanelCell rowCount={4}>
                                    {' '}
                                    <validatingElements.FilterDropdown
                                        name="normVersie"
                                        selectOptions={verificatieBerekeningSelectOptions.normVersie}
                                        topLabel="Norm versie"
                                    />
                                    <validatingElements.DependentSection showIf={(item) => item.normVersie === '7'}>
                                        <validatingElements.InputText name="normVersieAnders" />
                                    </validatingElements.DependentSection>
                                </EditPanelCell>
                                <EditPanelCell rowCount={4}>
                                    {' '}
                                    <validatingElements.FilterDropdown
                                        name="modelleerNiveau"
                                        selectOptions={verificatieBerekeningSelectOptions.modelleerNiveau}
                                        topLabel="Modelleer niveau"
                                    />{' '}
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelRow>
                                <EditPanelCell rowCount={4}>
                                    {' '}
                                    <validatingElements.FilterDropdown
                                        name="wegindeling"
                                        selectOptions={verificatieBerekeningSelectOptions.wegindeling}
                                        topLabel="Wegindeling"
                                    />{' '}
                                </EditPanelCell>
                                <EditPanelCell rowCount={4}>
                                    {' '}
                                    <validatingElements.FilterDropdown
                                        name="aantalZwareVoertuigenPerJaar"
                                        selectOptions={verificatieBerekeningSelectOptions.aantalZwareVoertuigenPerJaar}
                                        topLabel="Aantal zware voertuigen per jaar"
                                    />
                                    <validatingElements.DependentSection showIf={(item) => item.aantalZwareVoertuigenPerJaar === '9'}>
                                        <validatingElements.InputText name="aantalZwareVoertuigenPerJaarAnders" />
                                    </validatingElements.DependentSection>{' '}
                                </EditPanelCell>
                                <EditPanelCell rowCount={4}>
                                    {' '}
                                    <validatingElements.InputText name="restLevensduur" topLabel="Rest levensduur" />{' '}
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelRow>
                                <EditPanelCell rowCount={4}>
                                    {' '}
                                    <validatingElements.FilterDropdown
                                        name="beperkingVoertuigGewicht"
                                        selectOptions={verificatieBerekeningSelectOptions.beperkingVoertuigGewicht}
                                        topLabel="Beperking voertuig gewicht"
                                    />{' '}
                                </EditPanelCell>
                                <EditPanelCell rowCount={4}>
                                    {' '}
                                    <validatingElements.FilterDropdown
                                        name="beperkingAslast"
                                        selectOptions={verificatieBerekeningSelectOptions.beperkingAslast}
                                        topLabel="Beperking aslast"
                                    />{' '}
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelHeader text="Resultaat"></EditPanelHeader>
                            <EditPanelRow>
                                <EditPanelCell rowCount={4}>
                                    <ValidatingFormContext.Consumer>
                                        {(state) => (
                                            <ValidatingNumberFilterDropdown
                                                name="score"
                                                topLabel="Score overschrijven"
                                                selectOptions={verificatieBerekeningSelectOptions.toetsing}
                                                dropdownIndicatorStyle={getConditionStyle(state.item.score)}
                                            />
                                        )}
                                    </ValidatingFormContext.Consumer>
                                </EditPanelCell>
                                <EditPanelCell rowCount={8}>
                                    {' '}
                                    <validatingElements.TextArea name="opmerking" topLabel="Opmerking" />{' '}
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelHeader text="Documenten"></EditPanelHeader>
                            <EditPanelRow fixWidth={true}>
                                <EditPanelCell rowCount={12}>
                                    <validatingElements.AssetTemplateList
                                        name="bestanden"
                                        objectId={props.objectId}
                                        surveyId={props.surveyId}
                                        canEditDescription
                                        canRemove
                                    />
                                </EditPanelCell>

                                <EditPanelCell rowCount={12}>
                                    <validatingElements.AssetDropzone
                                        acceptedFiles=".png,.jpeg,.jpg,.xls,.xlsx,.pdf"
                                        objectId={props.objectId}
                                        surveyId={props.surveyId}
                                        name="bestanden"
                                    />
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelButtons>
                                <ValidatingEditPanelSaveButton small={true} />
                            </EditPanelButtons>
                        </validatingElements.ItemUrlForm>
                    </PanelBody>
                </CollapsiblePanel>
            </EditPanel>
        </Tray>
    );
}
